import { reactive } from "vue";
import { StarsMagnetService } from "@/services/stars_magnet.service";
import { emptyFilters, toISODate } from "./utils";

// Access is checked by the router guard and the Management menu; it's cached here.
export const smAccess = reactive({
  loaded: false,
  member: false,
  admin: false,
  async load(force = false) {
    if (this.loaded && !force) return this;
    const { member, admin } = await StarsMagnetService.getMyAccess();
    this.member = member;
    this.admin = admin;
    this.loaded = true;
    return this;
  },
  reset() {
    this.loaded = false;
    this.member = false;
    this.admin = false;
  },
});

const PER_PAGE = 50;
let pageSeq = 0;
let countSeq = 0;

// Shared state for the dashboard, its drawers and dialogs. The list (~114k
// talents) lives in the database: this holds one page, the counts, and every
// talent seen so far (cache) for the drawer and bulk actions.
export const sm = reactive({
  loaded: false,
  error: null,
  pageLoading: false,
  rows: [],
  total: 0,
  counts: null,
  options: { countries: [], agencies: [], platforms: [] },
  members: [],
  cache: {},
  filters: emptyFilters(),
  stage: null, // pipeline tile filter
  sort: { field: "completed_campaigns", desc: true },
  page: { first: 0, rows: PER_PAGE },

  // The filters as the database reads them (sm_filtered): no empty values, dates as YYYY-MM-DD.
  serverFilters() {
    const out = {};
    for (const [k, v] of Object.entries(this.filters)) {
      if (v === null || v === undefined || v === "" || v === false) continue;
      if (Array.isArray(v) && !v.length) continue;
      if (k === "swell" || k === "roster") {
        if (v !== "any") out[k] = v;
        continue;
      }
      out[k] = k === "statusFrom" || k === "statusTo" ? toISODate(v) : typeof v === "string" ? v.trim() : v;
    }
    if (this.stage) out.stage = this.stage;
    return out;
  },

  async load() {
    const [m, o] = await Promise.all([StarsMagnetService.getMembers(), StarsMagnetService.getFilterOptions()]);
    this.members = m.members;
    this.options = o.options;
    await Promise.all([this.fetchPage(), this.fetchCounts()]);
    this.error = this.error || m.error || o.error || null;
    this.loaded = !this.error;
  },

  async fetchPage() {
    const seq = ++pageSeq;
    this.pageLoading = true;
    const { rows, total, error } = await StarsMagnetService.getPage(this.serverFilters(), {
      sort: this.sort.field,
      desc: this.sort.desc,
      offset: this.page.first,
      limit: this.page.rows,
    });
    if (seq !== pageSeq) return; // a newer request has been sent
    this.pageLoading = false;
    if (error) {
      this.error = error;
      return;
    }
    this.error = null;
    for (const t of rows) this.cache[t.influencer_id] = t;
    this.rows = rows;
    this.total = total;
  },

  async fetchCounts() {
    const seq = ++countSeq;
    const { counts, error } = await StarsMagnetService.getCounts(this.serverFilters());
    if (seq !== countSeq || error) return;
    this.counts = counts;
  },

  // New filters: back to the first page, then reload the page and the counts.
  async reload({ resetPage = true } = {}) {
    if (resetPage) this.page.first = 0;
    await Promise.all([this.fetchPage(), this.fetchCounts()]);
  },

  talent(id) {
    return this.cache[id] || null;
  },
  async refreshTalent(influencerId) {
    const { talent } = await StarsMagnetService.getTalent(influencerId);
    if (talent) this.cache[influencerId] = talent;
    else delete this.cache[influencerId];
    const i = this.rows.findIndex((t) => t.influencer_id === influencerId);
    if (i >= 0) {
      if (talent) this.rows.splice(i, 1, talent);
      else this.rows.splice(i, 1);
    }
  },
  async refreshTalents(ids) {
    await Promise.all(ids.map((id) => this.refreshTalent(id)));
    this.fetchCounts();
  },
  async reloadMembers() {
    const { members } = await StarsMagnetService.getMembers();
    this.members = members;
  },

  get leads() {
    return this.members.filter((m) => m.is_lead);
  },
  resetFilters() {
    this.filters = emptyFilters();
    this.stage = null;
  },
});
